import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTA } from "@/components/CTA";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";
import { resolveAssetUrl } from "@/lib/media";

const POSTS_PER_PAGE = 9;

interface Post {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  excerpt: string | null;
  author: string;
  published_at: string;
  category: { name: string } | null;
  category_id: string | null;
  arabic_title: string | null;
  arabic_excerpt: string | null;
}

export default function BlogIndex() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { lang, t, localePath } = useLanguage();

  const { canonical, hreflang } = buildHreflang("/blog");
  useCmsPageSEO({
    title: lang === "ar"
      ? "مدوّنة وافاي | الويب والتجارة الإلكترونية والذكاء الاصطناعي في الكويت"
      : "Wavai Blog | Web, eCommerce & AI in Kuwait",
    description: lang === "ar"
      ? "رؤى حول شوبيفاي وووردبريس وويبفلو والذكاء الاصطناعي من وافاي — وكالة الكويت المتميّزة للويب والتجارة الإلكترونية."
      : "Insights on Shopify, WordPress, Webflow and AI from Wavai — Kuwait's premium web and eCommerce agency.",
    canonical,
    hreflang,
    keywords: "Wavai blog, Shopify Kuwait blog, WordPress Kuwait, AI Kuwait, ecommerce blog GCC",
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    const { data, error } = await supabase
      .from("blog_posts")
      .select("id, title, slug, cover_image, excerpt, author, published_at, category_id, arabic_title, arabic_excerpt, blog_categories(name)")
      .eq("status", "published")
      .order("published_at", { ascending: false });

    if (!error && data) {
      setPosts(data.map((p: any) => ({ ...p, category: p.blog_categories })));
    }
    setLoading(false);
  };

  const categories = useMemo(() => {
    const cats = Array.from(new Set(posts.map((p) => p.category?.name).filter(Boolean))) as string[];
    return cats.sort();
  }, [posts]);

  const filteredPosts = useMemo(() => {
    return activeCategory
      ? posts.filter((p) => p.category?.name === activeCategory)
      : posts;
  }, [activeCategory, posts]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = filteredPosts.slice(0, page * POSTS_PER_PAGE);
  const hasMore = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    setTimeout(() => {
      setPage((p) => p + 1);
      setLoadingMore(false);
    }, 300);
  }, [hasMore, loadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleCategoryChange = (cat: string | null) => {
    setActiveCategory(cat);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Header />
      <main className="overflow-x-hidden">
        {/* Hero — matches Work index */}
        <section className="pt-28 pb-8 sm:pb-12 lg:pt-40 lg:pb-20 relative overflow-x-hidden">
          <div className="absolute inset-0 bg-gradient-hero opacity-40" />
          <div className="container px-4 sm:px-6 relative z-10">
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-xs sm:text-sm uppercase tracking-[0.18em] text-primary font-medium mb-3 sm:mb-4">
                {t("Insights", "رؤى")}
              </p>
              <h1 className="text-[2.1rem] sm:text-4xl lg:text-5xl xl:text-6xl font-display font-bold mb-3 sm:mb-4 lg:mb-6 leading-[1.02] tracking-tight text-balance">
                {t("The Wavai", "مدوّنة")} <span className="text-gradient">{t("Blog", "Wavai")}</span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-lg mx-auto leading-relaxed">
                {t(
                  "Insights on ecommerce, AI, marketing, and digital transformation from our team.",
                  "رؤى حول التجارة الإلكترونية والذكاء الاصطناعي والتسويق والتحوّل الرقمي من فريقنا."
                )}
              </p>
            </div>
          </div>
        </section>

        {/* Filter pills — matches Work index */}
        <section className="py-6 sm:py-8 border-y border-border/50 bg-background/95">
          <div className="container px-4 sm:px-6 overflow-hidden">
            <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1 pr-20 sm:pr-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0">
              <button
                onClick={() => handleCategoryChange(null)}
                className={`flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                  !activeCategory ? "bg-primary text-primary-foreground shadow-sm" : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {t("All", "الكل")}
              </button>
              {categories.map((cat) => {
                const IconComp = getCategoryIcon(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      activeCategory === cat ? "bg-primary text-primary-foreground shadow-sm" : "bg-secondary text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <IconComp size={16} weight="duotone" />
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Grid — matches Work index card style */}
        <section className="py-8 sm:py-12 lg:py-32">
          <div className="container px-4 sm:px-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-2.5 lg:gap-3">
                  {paginatedPosts.map((post) => {
                    const displayTitle = lang === "ar" && post.arabic_title ? post.arabic_title : post.title;
                    const displayExcerpt = lang === "ar" && post.arabic_excerpt ? post.arabic_excerpt : post.excerpt;
                    const imageSrc = resolveAssetUrl(post.cover_image || "/placeholder.svg");
                    const formattedDate = new Date(post.published_at).toLocaleDateString(
                      lang === "ar" ? "ar-SA" : "en-US",
                      { year: "numeric", month: "long", day: "numeric" }
                    );

                    return (
                      <Link
                        key={post.id}
                        to={localePath(`/blog/${post.slug}`)}
                        className="group block w-full max-w-full rounded-2xl bg-card border border-transparent hover:border-primary/30 transition-all duration-300 hover:shadow-glow overflow-hidden"
                      >
                        <div className="p-2 sm:p-3 pb-0">
                          <div className="aspect-[16/11] overflow-hidden bg-secondary/50 border border-border/60 rounded-none group-hover:rounded-xl transition-[border-radius] duration-150 ease-out">
                            {post.cover_image ? (
                              <img
                                src={imageSrc}
                                alt={displayTitle}
                                className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-secondary">
                                {(() => {
                                  const CatIcon = getCategoryIcon(post.category?.name || "");
                                  return <CatIcon size={48} weight="duotone" className="text-muted-foreground/30" />;
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="p-4 sm:p-5">
                          <div className="flex items-center gap-2 mb-2">
                            {post.category?.name && (() => {
                              const CatIcon = getCategoryIcon(post.category!.name);
                              return (
                                <Badge variant="secondary" className="text-xs inline-flex items-center gap-1">
                                  <CatIcon size={12} weight="duotone" />
                                  {post.category!.name}
                                </Badge>
                              );
                            })()}
                          </div>
                          <h3 className="font-sans font-medium text-base group-hover:text-primary transition-colors mb-1.5 line-clamp-2">
                            {displayTitle}
                          </h3>
                          {displayExcerpt && (
                            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed mb-3">
                              {displayExcerpt}
                            </p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span>{post.author}</span>
                            <span>·</span>
                            <span>{formattedDate}</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>

                <div ref={sentinelRef} className="w-full py-8 flex justify-center">
                  {loadingMore && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
                  {!hasMore && paginatedPosts.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {t("You've reached the end", "لقد وصلت إلى النهاية")}
                    </p>
                  )}
                </div>

                {paginatedPosts.length === 0 && (
                  <p className="text-center text-muted-foreground py-12">
                    {t("No posts found.", "لم يتم العثور على مقالات.")}
                  </p>
                )}
              </>
            )}
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
